import {
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  ACTIVE_INACTIVE_HELP_TOPICS,
  DELETE_HELP_TOPICS,
  GET_ALL_HELP_TOPICS,
  GLOBAL_DROP_DOWN,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
  toastSuccess,
} from "../../utils/AppFunctions";
export interface IHelpTopic {
  id: string;
  topic_title: string;
  topic_description: string;
  help_category_id: string;
  help_category_name: string;
  display_sequence: number;
  reference_link: string | null;
  is_active: TBitValue;
  tags: string[];
}

export function getHelpTopicsFields<T>(
  actionComponent: (item: T) => JSX.Element,
  handleHelpTopicStatus: (item: T) => void,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("topic_title"),
      key: "topic_title",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("help_category"),
      key: "help_category_name",
      sort: false,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("display_sequence"),
      key: "display_sequence",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("status"),
      key: "is_active",
      sort: false,
      type: TABLE_FIELD_TYPE.switch,
      align: TABLE_FIELD_ALIGN.center,
      checkedValue: "1",
      flex: "col-span-2",
      handleCheck: handleHelpTopicStatus,
    },
    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.right,
      flex: "col-span-1",
      component: actionComponent,
    },
  ];
}

export const getAllHelpTopics = async (
  setHelpTopicsList: TReactSetState<IHelpTopic[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  helpCategoryId?: string
) => {
  try {
    const response = await GET_ALL_HELP_TOPICS(
      pagination,
      search,
      helpCategoryId
    );
    if (
      response &&
      response.code === DEFAULT_STATUS_CODE_SUCCESS &&
      response.data
    ) {
      setHelpTopicsList(response.data.items);
      setPagination(response.data.pagination);
    } else {
      setHelpTopicsList([]);
      sweetAlertError({
        message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllHelpTopics");
  }
};

export const activeInactiveHelpTopics = async (
  id: string,
  value: TBitValue
) => {
  try {
    const response = await ACTIVE_INACTIVE_HELP_TOPICS(id, {
      is_active: value,
    });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      toastSuccess(response.message);
      return true;
    } else {
      sweetAlertError({
        message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (error) {
    handleError(error, "activeInactiveHelpTopics");
    return false;
  }
};

export const deleteHelpTopics = async (id: string) => {
  try {
    const deleteData = await DELETE_HELP_TOPICS(id);
    if (deleteData && deleteData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deleteData.message });
      return true;
    } else {
      sweetAlertError({
        message: deleteData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "deleteHelpTopics");
    return false;
  }
};

export const getHelpCategoryDdl = async (
  setHelpCategory: TReactSetState<IOption<string>[]>,
  requiredId: string | null = null,
  showOnlyActive: boolean = false
) => {
  try {
    const payload = {
      entity: "help_category",
      data_value: "id",
      display_value: "category_title",
      filters: [
        {
          field: "is_active",
          value: showOnlyActive ? "1" : "1|0",
          multi_value: true,
        },
      ],
      required_ids: requiredId ? [requiredId] : [],
    };

    const helpCategoryDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      helpCategoryDDLList &&
      helpCategoryDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setHelpCategory(helpCategoryDDLList.data || []);
    } else {
      toastError(helpCategoryDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getHelpCategoryDdl");
  }
};
